<template>
  <b-card
    class="overflow-hidden"
    no-body
  >
    <b-table
      responsive
      :items="quotes"
      :fields="fieldsComputed"
      :sort-by="resolvedSortBy"
      :sort-desc="resolvedSortDesc"
      @sort-changed="handleSort"
    >
      <template #cell(folio)="data">
        <div class="quote-folio-cell">
          <quote-button-route :quote="data.item" />
          <quote-log-button
            v-if="isAdmin"
            :quote-id="data.item.id"
            :folio="data.item.folio"
          />
        </div>
      </template>

      <template #cell(customer_name)="data">
        {{ data.value || '—' }}
      </template>

      <template #cell(published_at)="data">
        {{ data.value | date }}
      </template>
      <template #cell(purchased_at)="data">
        {{ data.value | date }}
      </template>
      <template #cell(created_to_closed_label)="data">
        {{ data.value || '—' }}
      </template>
      <!-- Corre solo mientras la cotización no se ha cerrado -->
      <template #cell(open_elapsed)="data">
        <span
          v-if="isElapsedRunning(data.item)"
          class="open-elapsed"
        >{{ elapsedSince(data.item.created_at) }}</span>
        <span v-else>—</span>
      </template>
      <template #cell(days_overdue)="data">
        <b-badge :variant="data.value > 0 ? 'danger' : (data.value === 0 ? 'warning' : 'secondary')">
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(quote_status)="data">
        <!-- Si está eliminada -->
        <b-badge
          v-if="data.value === 'deleted'"
          variant="danger"
        >
          {{ $t('deleted') }}
        </b-badge>

        <!-- Si NO está eliminada -->
        <b-badge
          v-else
          :variant="$helpers.quoteStatusColor(data.value)"
        >
          {{ $helpers.quoteStatusName(data.value) }}
        </b-badge>
      </template>

      <!-- Si el cliente ve los nombres de los proveedores en esta cotización -->
      <template #cell(open_quotation_enabled)="data">
        <div
          class="open-quotation-cell"
          :title="visibilityTitle(data.item)"
        >
          <b-form-checkbox
            switch
            :checked="data.value"
            :disabled="isSavingVisibility(data.item.id)"
            class="mb-0 open-quotation-switch"
            @change="checked => toggleOpenQuotation(data.item, checked)"
          />
          <span
            v-if="data.item.open_quotation_overridden"
            class="open-quotation-pin"
            aria-hidden="true"
          >&#9679;</span>
        </div>
      </template>

      <template #cell(payment_status)="data">
        <!-- Si la cotización está eliminada, no mostramos nada -->
        <span v-if="data.item.quote_status === 'deleted'" />

        <!-- Si NO está eliminada, mostramos el status de pago normal -->
        <b-badge
          v-else
          :variant="$helpers.paymentStatusColor(data.value)"
        >
          {{ $helpers.paymentStatusName(data.value) }}
        </b-badge>
      </template>

      <template #cell(is_invoiced)="data">
        <div class="d-flex align-items-center">
          <b-badge
            :variant="$helpers.invoiceStatus(data.item).variant"
            class="w-100"
          >
            {{ $helpers.invoiceStatus(data.item).label }}
          </b-badge>
          <b-button
            variant="primary"
            class="p-0 ml-1 custom-padding"
            @click="
              $router.push({
                name: 'quoteInvoices',
                params: { quoteId: data.item.id },
              })
            "
          >
            <feather-icon
              icon="EyeIcon"
              class="text-white"
              size="18"
              color="#000"
            />
          </b-button>
          <b-button
            variant="primary"
            class="p-0 custom-padding"
            style="margin-left:6px"
            @click="openModal(data.item)"
          >
            <feather-icon
              icon="UploadIcon"
              class="text-white"
              size="18"
            />
          </b-button>
        </div>
      </template>
      <template #cell(is_supplier_fully_invoiced)="data">
        <div class="d-flex align-items-center">
          <b-badge
            :variant="data.value ? 'success' : 'secondary'"
            class="w-100"
          >
            {{ data.value ? $t('invoiced') : $t('pending') }}
            {{ data.item.invoiced_purchase_orders_count }} /
            {{ data.item.total_purchase_orders }}
          </b-badge>
          <b-button
            variant="primary"
            class="p-0 ml-1 custom-padding"
            @click="
              $router.push({
                name: 'purchaseOrders',
                params: { quoteId: data.item.id },
              })
            "
          >
            <feather-icon
              icon="EyeIcon"
              class="text-white"
              size="18"
              color="#000"
            />
          </b-button>
        </div>
      </template>
      <template #cell(total)="data">
        $ {{ data.value | money }}
      </template>
      <template
        v-if="toolsEnabled"
        #cell(tools)="data"
      >
        <b-dropdown
          id="dropdown-1"
          size="sm"
          dropright
          no-caret
          variant="outline-none"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="18"
              color="#000"
            />
          </template>
          <b-dropdown-item
            v-if="allowDelete && data.item.quote_status !== 'deleted'"
            @click="removeQuote(data.item)"
          >{{ $t('delete') }}</b-dropdown-item>
          <b-dropdown-item
            v-if="allowRestore && data.item.quote_status === 'deleted'"
            @click="restoreDeletedQuote(data.item.id)"
          >{{ $t('restore') }}</b-dropdown-item>
          <!-- <b-dropdown-item
            :to="{ name: 'editQuote', params: { quoteId: data.item.id } }"
          >{{ $t('edit') }}</b-dropdown-item>
          <b-dropdown-item
            :to="{
              name: 'addProductsToQuote',
              params: { quoteId: data.item.id },
            }"
          >{{ $t('add', { type: $t('products') }) }}</b-dropdown-item> -->
        </b-dropdown>
      </template>
    </b-table>
    <b-modal
      id="newInvoice"
      ok-only
      :ok-title="$t('save')"
      @ok="saveFiles"
    >
      <div class="text-center mb-2">
        <h3>{{ $t('uploadYourInvoice') }}</h3>
      </div>
      <b-row>
        <b-col sm="12">
          <b-form-group>
            <b-form-datepicker
              v-model="newInvoice.date"
              :placeholder="$t('date')"
            />
          </b-form-group>
        </b-col>
        <b-col
          sm="12"
          class="mt-1"
        >
          <dropzone
            height="150"
            :multiple="true"
          />
        </b-col>
      </b-row>
    </b-modal>
  </b-card>
</template>

<script>
import { BTable } from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Dropzone from '@/components/dropzone/Dropzone.vue'
import QuoteButtonRoute from '@/components/pricing/QuoteButtonRoute.vue'
import QuoteLogButton from '@/components/quotes/QuoteLogButton.vue'

export default {
  components: {
    BTable,
    Dropzone,
    QuoteButtonRoute,
    QuoteLogButton,
  },
  props: {
    quotes: {
      type: Array,
      default() {
        return []
      },
    },
    noTools: {
      type: Boolean,
      default() {
        return false
      },
    },
    allowDelete: {
      type: Boolean,
      default: false,
    },
    allowRestore: {
      type: Boolean,
      default: false,
    },
    showPurchasedDate: {
      type: Boolean,
      default: false,
    },
    showDaysOverdue: { type: Boolean, default: false },
    showVisibility: { type: Boolean, default: false },
    showCreatedToClosedDuration: { type: Boolean, default: false },
    initialSortBy: { type: String, default: null },
    initialSortDesc: { type: Boolean, default: null },
  },
  data() {
    return {
      newInvoice: {
        quoteId: null,
        name: '',
        folio: '',
        file: null,
        date: null,
      },
      savingVisibilityIds: [],
      now: Date.now(),
      elapsedTimer: null,
      baseFields: [
        { key: 'folio', label: 'Folio', sortable: true },
        { key: 'customer_name', label: this.$t('customer'), sortable: false },
        {
          key: this.showPurchasedDate ? 'purchased_at' : 'published_at',
          label: this.showPurchasedDate ? this.$t('purchasedDate') : this.$t('publishedDate'),
          sortable: true,
        },
        // "days_overdue" se insertará dinámicamente si showDaysOverdue = true
        { key: 'quote_status', label: this.$t('status') },
        { key: 'payment_status', label: this.$t('paymentStatus') },
        { key: 'is_invoiced', label: this.$t('customerInvoice') },
        // { key: 'is_supplier_fully_invoiced', label: this.$t('supplierInvoice') },
        { key: 'total', label: this.$t('total'), sortable: true },
        { key: 'tools', label: this.$t('actions') },
      ],
    }
  },

  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('suppliers', {
      files: 'newProviderFiles',
    }),

    isAdmin() {
      return this.currentUser?.role_name === 'admin'
    },

    fieldsComputed() {
      // Clonar para no mutar data()
      const fields = [...this.baseFields]

      if (this.showDaysOverdue) {
        // Insertar "days_overdue" después de la fecha
        const dateIdx = fields.findIndex(f => f.key === 'purchased_at' || f.key === 'published_at')
        const insertAt = dateIdx >= 0 ? dateIdx + 1 : 1
        fields.splice(insertAt, 0, {
          key: 'days_overdue',
          label: this.$t('daysOverdue') || 'Días vencidos',
          sortable: true,
        })
      }

      // Respetar noTools
      if (!this.toolsEnabled) {
        const idx = fields.findIndex(f => f.key === 'tools')
        if (idx > -1) fields.splice(idx, 1)
      }

      if (this.showVisibility) {
        const statusIndex = fields.findIndex(f => f.key === 'quote_status')
        fields.splice(statusIndex + 1, 0, {
          key: 'open_quotation_enabled',
          label: 'Visible',
          thStyle: { width: '78px' },
          tdClass: 'text-center',
          thClass: 'text-center',
        })
      }

      if (this.showCreatedToClosedDuration) {
        fields.push({
          key: 'created_to_closed_label',
          label: this.$t('createdToClosedDuration'),
          sortable: false,
        })
        fields.push({
          key: 'open_elapsed',
          label: 'Lleva abierta',
          sortable: true,
        })
      }

      return fields
    },

    resolvedSortBy() {
      return this.initialSortBy || null
    },
    resolvedSortDesc() {
      return this.initialSortDesc !== null ? this.initialSortDesc : null
    },
    toolsEnabled() {
      return !this.noTools && (this.allowDelete || this.allowRestore)
    },
  },

  created() {
    // fieldsComputed controls the tools column visibility
  },

  mounted() {
    // Un solo intervalo para toda la tabla; solo mueve this.now y Vue repinta la columna.
    if (!this.showCreatedToClosedDuration) return

    this.elapsedTimer = setInterval(() => {
      this.now = Date.now()
    }, 1000)
  },

  beforeDestroy() {
    if (this.elapsedTimer) clearInterval(this.elapsedTimer)
  },

  methods: {
    ...mapActions('quotes', [
      'deleteQuote',
      'fetchQuotes',
      'restoreQuote',
      'createQuoteInovice',
      'updateQuoteOpenQuotation',
    ]),

    // Una cotizacion eliminada no tiene subasta corriendo, asi que no cuenta tiempo por mas
    // vieja que sea. Se revisan las dos marcas de borrado porque conviven en los datos:
    // unas traen deleted_at y otras solo el estatus.
    isElapsedRunning(quote) {
      if (!quote?.created_at || quote.bidding_closed_at) return false

      return !quote.deleted_at && quote.quote_status !== 'deleted'
    },

    // Depende de this.now, que avanza cada segundo, para que Vue repinte la celda sola.
    elapsedSince(createdAt) {
      const start = new Date(createdAt).getTime()
      if (Number.isNaN(start)) return '—'

      const total = Math.max(0, Math.floor((this.now - start) / 1000))
      const days = Math.floor(total / 86400)
      const hours = Math.floor((total % 86400) / 3600)
      const minutes = Math.floor((total % 3600) / 60)
      const seconds = total % 60
      const pad = value => String(value).padStart(2, '0')

      return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    },

    // Sin texto en la celda para no robar ancho: el estado se explica al pasar el mouse.
    visibilityTitle(quote) {
      const estado = quote.open_quotation_enabled
        ? 'El cliente ve los nombres de los proveedores'
        : 'El cliente ve los proveedores sin nombre'
      const origen = quote.open_quotation_overridden
        ? ' · fijada por un admin'
        : ' · heredada del cliente'

      return estado + origen
    },

    isSavingVisibility(quoteId) {
      return this.savingVisibilityIds.includes(quoteId)
    },

    // Se pinta el cambio de inmediato y se revierte si el backend lo rechaza: el renglon
    // vive en la prop quotes, asi que no hay refetch de por medio.
    async toggleOpenQuotation(quote, checked) {
      if (!quote?.id || this.isSavingVisibility(quote.id)) return

      const previous = quote.open_quotation_enabled
      this.savingVisibilityIds = [...this.savingVisibilityIds, quote.id]
      this.$set(quote, 'open_quotation_enabled', checked)

      try {
        const updated = await this.updateQuoteOpenQuotation({
          quoteId: quote.id,
          openQuotation: checked,
        })

        this.$set(quote, 'open_quotation', updated.open_quotation)
        this.$set(quote, 'open_quotation_enabled', updated.open_quotation_enabled)
        this.$set(quote, 'open_quotation_overridden', updated.open_quotation_overridden)
      } catch (error) {
        this.$set(quote, 'open_quotation_enabled', previous)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No se pudo cambiar la visibilidad de la cotización.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.savingVisibilityIds = this.savingVisibilityIds.filter(id => id !== quote.id)
      }
    },

    handleSort(ctx) {
      this.$emit('refetch', {
        sortBy: ctx.sortBy,
        sortDesc: ctx.sortDesc,
      })
    },

    removeQuote(quote) {
      const confirmed = window.confirm(
        this.$t('deleteQuoteConfirmation') || '¿Deseas eliminar la cotización?',
      )
      if (!confirmed) return

      const deleteReason = window.prompt(
        this.$t('deleteQuoteReasonPrompt') || 'Motivo de eliminación (opcional)',
      )
      if (deleteReason === null) return

      this.deleteQuote({
        quoteId: quote.id,
        deleteReason,
      }).then(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('quoteDeletedSuccessfully'),
            icon: 'Trash2Icon',
            variant: 'success',
          },
        })
        this.$emit('refetch')
      })
    },

    restoreDeletedQuote(quoteId) {
      const confirmed = window.confirm(
        this.$t('restoreQuoteConfirmation') || '¿Deseas restaurar la cotización eliminada?',
      )
      if (!confirmed) return

      this.restoreQuote(quoteId).then(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('quoteRestoredSuccessfully'),
            icon: 'RotateCcwIcon',
            variant: 'success',
          },
        })
        this.$emit('refetch')
      })
    },

    openModal(quote) {
      this.newInvoice = {
        quoteId: quote.id,
        name: '',
        folio: '',
        file: null,
        date: new Date(),
      }
      this.$bvModal.show('newInvoice')
    },
    saveFiles() {
      const files = new FormData()
      this.files.forEach(file => {
        files.append('invoice_attachments[]', file)
      })
      files.append('invoiced_at', this.newInvoice.date)
      this.createQuoteInovice({
        quoteId: this.newInvoice.quoteId,
        attachments: files,
      }).then(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('filesSaved'),
            icon: 'SaveIcon',
            variant: 'success',
          },
        })
        this.$emit('refetch')
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.quote-folio-cell {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.25rem;
}

.open-quotation-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.open-quotation-switch {
  flex: 0 0 auto;
  // el switch de bootstrap-vue reserva ancho para una etiqueta que aqui no existe
  ::v-deep .custom-control-label { padding-left: 0; }
  padding-left: 1.6rem;
}

// Marca discreta de que un admin fijo el valor en vez de heredarlo del cliente.
.open-quotation-pin {
  font-size: 0.5rem;
  color: #b2aebf;
  line-height: 1;
}

// La tabla trae hasta 12 columnas; sin esto no cabe sin scroll horizontal.
::v-deep .table {
  font-size: 0.82rem;

  th,
  td {
    padding: 0.5rem 0.4rem;
    vertical-align: middle;
  }

  th {
    font-size: 0.68rem;
    letter-spacing: 0.2px;
  }
}

.open-elapsed,
::v-deep td .open-elapsed {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.custom-padding {
  padding: 1px !important;
}
</style>
